const getisLoggedIn = () => {
  // Verifică în toate spațiile de stocare pentru a vedea dacă utilizatorul este logat
  let isLoggedIn = sessionStorage.getItem('isLoggedIn') || localStorage.getItem('isLoggedIn') || getCookie('_isLoggedInCjpc');
  return isLoggedIn === 'true';
};

const getCookie = (name) => {
  // Funcția getCookie
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};
const deleteCookie = (name) => {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
};

let alreadyChecked = false;
let lastCheckTime = 0;
let currentValidationPromise = null; // Promise pentru validarea în curs
const CHECK_INTERVAL = 60000; // Verifică token-ul doar o dată la 60 secunde
const MAX_RETRIES = 4; // Încercăm de 4 ori înainte să delogăm
const RETRY_DELAY = 2000; // 2 secunde între încercări

/**
 * ✅ FIX-uri aplicate pentru NS_BINDING_ABORTED:
 * 
 * 1. AbortError e IGNORAT complet (nu e eroare reală, e timeout/navigare)
 * 2. alreadyChecked se resetează ÎNAINTE de retry (nu se mai blochează)
 * 3. Logout-ul se face prin event 'force-logout' (un singur loc de redirect)
 * 4. NS_BINDING_ABORTED pe OPTIONS e normal (CORS preflight)
 * 
 * Comportament așteptat:
 * - NS_BINDING_ABORTED în DevTools = NORMAL (ignoră-l)
 * - Doar 401/500 pe request-ul FINAL = PROBLEMĂ
 */

// Funcție helper pentru a reseta timpul de verificare (apelează-o când știi sigur că conexiunea e OK)
export const resetTokenRetries = () => {
  lastCheckTime = 0;
};

/**
 * Așteaptă ca verificarea token-ului să se termine (dacă e în curs)
 * Folosește asta înainte de a face cereri importante
 */
export const waitForTokenValidation = async () => {
  if (currentValidationPromise) {
    try {
      await currentValidationPromise;
    } catch (err) {
      // Ignoră erorile - ne interesează doar să așteptăm
    }
  }
};

const verificareToken = () => {
  // Verifică dacă utilizatorul este logat
  if (!getisLoggedIn()) {
    return { token: null, ok2goval: null };
  }

  // Verifică token-ul în toate spațiile de stocare
  let token = sessionStorage.getItem('token') || localStorage.getItem('token') || getCookie('_tokenCjpc');
  let ok2goval = sessionStorage.getItem('ok2go') || localStorage.getItem('ok2go') || getCookie('ok2go');

  if (!token) {
    return { token: null, ok2goval: null };
  }

  if (ok2goval === 'false') {
    if (window.location.pathname === '/logout') {
      // Dacă utilizatorul este pe /logout și ok2go este false, ștergem tot ce este legat de ok2go
      localStorage.removeItem('ok2go');
      sessionStorage.removeItem('ok2go');
      deleteCookie('ok2go');  
    } else if (window.location.pathname !== '/licentiere') {
      // Dacă nu suntem pe /licentiere și ok2go este false, redirecționăm la /licentiere
      window.location.href = '/licentiere';
      return { token, ok2goval };  
    }
  }
  
   // ✅ Fă un singur fetch dacă tokenul este prezent și validabil
   // Verifică doar dacă a trecut suficient timp de la ultima verificare
   const currentTime = Date.now();
   const shouldCheck = currentTime - lastCheckTime > CHECK_INTERVAL;
   
   if (token && ok2goval === 'true' && shouldCheck) {
    // Setăm lastCheckTime IMEDIAT pentru a preveni verificări multiple
    lastCheckTime = currentTime;
    
    // Verificăm dacă nu e deja o verificare în curs
    if (alreadyChecked) {
      return { token, ok2goval };
    }
    
    alreadyChecked = true;

    // Creează un Promise pentru validarea în curs
    currentValidationPromise = new Promise((resolveValidation) => {
      const attemptTokenValidation = (attempt = 0) => {
        // Timeout pentru fetch - 10 secunde maxim
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000);

        fetch(`${window.API_URL}user`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Accept': 'application/json',
          },
          signal: controller.signal,
          priority: 'high'
        })
          .then((res) => res.json().then((body) => ({ status: res.status, body })))
          .then(({ status, body }) => {
            clearTimeout(timeoutId);
            alreadyChecked = false;
            currentValidationPromise = null;
            resolveValidation();
            
            if (status === 401 || body?.message === 'Utilizatorul nu este logat!') {
              console.warn('🔒 Token invalid. Se face redirect la logout...');
              
              // ✅ FIX 3: Emit event în loc de redirect direct
              window.dispatchEvent(new CustomEvent('force-logout', { detail: { reason: 'invalid_token' } }));
            } else if (status === 429) {
              // Too Many Requests - reîncearcă cu exponential backoff
              if (attempt < MAX_RETRIES) {
                const backoffDelay = RETRY_DELAY * Math.pow(2, attempt); // 2s, 4s, 8s
                console.warn(`⚠️ Too Many Requests (429) - reîncearcă în ${backoffDelay}ms (încercarea ${attempt + 1}/${MAX_RETRIES})`);
                
                // ✅ FIX 2: Resetează alreadyChecked înainte de retry
                alreadyChecked = false;
                
                setTimeout(() => {
                  attemptTokenValidation(attempt + 1);
                }, backoffDelay);
              } else {
                console.error(`💥 Too Many Requests (429) după ${MAX_RETRIES} încercări`);
              }
            }
          })
          .catch((err) => {
            clearTimeout(timeoutId);
            
            // ✅ FIX 1: Ignoră complet AbortError (comportament normal)
            if (err.name === 'AbortError') {
              console.log('⏱️ Request abortat (timeout sau navigare) - IGNORAT');
              alreadyChecked = false;
              currentValidationPromise = null;
              resolveValidation();
              return;
            }
            
            // Verifică dacă e eroare de rețea reală (nu abort)
            const isNetworkError = 
              err.message?.includes('NetworkError') ||
              err.message?.includes('Failed to fetch');
            
            if (isNetworkError && attempt < MAX_RETRIES) {
              console.warn(`⚠️ Eroare de rețea la validarea tokenului (încercarea ${attempt + 1}/${MAX_RETRIES}):`, err.message);
              
              // ✅ FIX 2: Resetează alreadyChecked înainte de retry
              alreadyChecked = false;
              
              setTimeout(() => {
                attemptTokenValidation(attempt + 1);
              }, RETRY_DELAY);
            } else if (attempt >= MAX_RETRIES) {
              console.error(`💥 Eroare repetată la validarea tokenului după ${MAX_RETRIES} încercări:`, err);
              alreadyChecked = false;
              currentValidationPromise = null;
              resolveValidation();
              
              // ✅ FIX 3: Emit event în loc de redirect direct
              window.dispatchEvent(new CustomEvent('force-logout', { detail: { reason: 'max_retries' } }));
            } else {
              console.error('💥 Eroare critică la validarea tokenului:', err);
              alreadyChecked = false;
              currentValidationPromise = null;
              resolveValidation();
              
              // ✅ FIX 3: Emit event în loc de redirect direct
              window.dispatchEvent(new CustomEvent('force-logout', { detail: { reason: 'critical_error' } }));
            }
          });
      };

      // Începe prima încercare
      attemptTokenValidation(0);
    });
  }

  return { token, ok2goval };
};

export default verificareToken;
